import { useCallback, useEffect } from 'react';

import { useChatOverlayContext } from '../context/chatOverlayContext';
import { useIspmContext } from '../context/ispmContext';
import callChatAskApi from '../data-layer/savi-chat-ask/api.service';
import callSampleDataApi from '../data-layer/savi-sample-date/api.service';
import {
  type AskApiRequest,
  type LowConfidenceChartAskApiResponse,
  type LowConfidenceTabularAskApiResponse,
  type SampleDataApiRequest,
} from '../data-layer/types';
import { getSaviApiUrlLists, Logger } from '../services';
import { getChatHistory } from '../services/chatHistory';

import useApiCall from './use-apicall';

/**
 * Custom hook that provides access to various API calls used in the chat overlay.
 *
 * This hook utilizes the chat and ISPM contexts and integrates with specific API services.
 * It handles chat history, low confidence responses, and general chat API requests.
 *
 * @returns {object} - An object containing two functions for handling chat API and sample data API requests:
 *   - `handleChatAskApiInChatOverlay`: Handles making a chat ask API call.
 *   - `handleLowConfidenceSampleDataApi`: Handles making a sample data API call for low-confidence responses.
 */
const useSaviApi = () => {
  const { ispmState } = useIspmContext();
  const { updateChatOverlayState } = useChatOverlayContext();

  const { handleApiCall } = useApiCall();

  const { CHAT_ASK_URL, DATA_SAMPLE_URL } = getSaviApiUrlLists(
    ispmState.useMockServerApis
  );

  /**
   * useEffect to load initial messages from session storage when the component mounts.
   *
   * This effect ensures that chat messages stored in session (from previous chat interactions)
   * are loaded into the chat overlay on component mount.
   *
   * @effect
   * @function loadInitialChatHistory
   */
  useEffect(() => {
    const initialMessages = getChatHistory();

    if (initialMessages.length > 0) {
      updateChatOverlayState('messages', initialMessages);
    }
  }, [updateChatOverlayState]);

  /**
   * Makes an API call for low-confidence chart or tabular responses and retrieves sample data.
   *
   * This function is triggered when the chat bot receives a low-confidence response and requires
   * sample data (such as chart specs or tabular data) to be fetched via an API call.
   *
   * @function handleLowConfidenceSampleDataApi
   *
   * @param {LowConfidenceChartAskApiResponse | LowConfidenceTabularAskApiResponse} lowConfidenceData -
   *   The low-confidence data that contains the query, chat UUID, and detailed steps.
   *
   * @returns {void}
   */
  const handleLowConfidenceSampleDataApi = useCallback(
    (
      lowConfidenceData:
        | LowConfidenceChartAskApiResponse
        | LowConfidenceTabularAskApiResponse
    ) => {
      // Prepare the request payload for sample data API
      let requestSampleData: SampleDataApiRequest = {
        chatuuid: lowConfidenceData.chatuuid,
        query: lowConfidenceData.query,
        detailedSteps: lowConfidenceData.detailedSteps,
        confidence: lowConfidenceData.confidence,
      };

      // If the low confidence data contains chart specs, add them to the request
      if ('chartSpec' in lowConfidenceData) {
        requestSampleData = {
          ...requestSampleData,
          chartSpec: lowConfidenceData.chartSpec,
        };
      }

      // Make the API call to retrieve sample data
      handleApiCall(requestSampleData, callSampleDataApi, DATA_SAMPLE_URL)
        .then(() =>
          Logger.info('handleLowConfidenceSampleDataApi request succeded')
        )
        .catch((error: Error) =>
          Logger.info('callSampleDataApi request failed', { error })
        );
    },
    [DATA_SAMPLE_URL, handleApiCall]
  );

  /**
   * Makes a chat ask API call within the chat overlay.
   *
   * This function is responsible for making the chat ask API call when the user submits a query
   * in the chat overlay. It checks for empty queries and logs appropriate information.
   *
   * @function handleChatAskApiInChatOverlay
   *
   * @param {AskApiRequest} askRequest - The request payload containing the user's free text query and chat UUID.
   *
   * @returns {void}
   */
  const handleChatAskApiInChatOverlay = useCallback(
    (askRequest: AskApiRequest) => {
      if (askRequest.freeText.length === 0) {
        Logger.info(
          'handleChatAskApiInChatOverlay --> No query text available to call chatask'
        );

        return;
      }

      // Make the API call to process the chat query
      handleApiCall(askRequest, callChatAskApi, CHAT_ASK_URL)
        .then(() => Logger.info('call chat Ask request succeded'))
        .catch((error: Error) =>
          Logger.info('call chat ask request failed', { error })
        );
    },
    [CHAT_ASK_URL, handleApiCall]
  );

  // Return both API handling functions for use in the component
  return {
    handleChatAskApiInChatOverlay,
    handleLowConfidenceSampleDataApi,
  };
};

export default useSaviApi;
